import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Brain, Zap, CheckCircle2, XCircle, ChevronRight, Trophy, RotateCcw, Loader2, Clock, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";

const LEVELS = ["easy", "medium", "hard", "hots"];
const LEVEL_LABEL = { easy: "Mudah", medium: "Sedang", hard: "Sulit", hots: "HOTS" };
const LEVEL_COLOR = {
  easy: "text-su-success bg-su-success/15",
  medium: "text-su-primary bg-su-primary/15",
  hard: "text-su-energy bg-su-energy/15",
  hots: "text-su-danger bg-su-danger/15",
};

export default function AdaptivePractice() {
  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [exam, setExam] = useState("CPNS");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [fb, setFb] = useState(null); // feedback after answering
  const [progress, setProgress] = useState({ index: 0, total: 20, correct: 0, wrong: 0 });
  const [level, setLevel] = useState("medium");
  const [summary, setSummary] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [history, setHistory] = useState(null);
  const timerRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);
  const loadHistory = () => api.get("/adaptive/history").then(({ data }) => setHistory(data)).catch(() => setHistory({ sessions: [], stats: {} }));
  useEffect(() => { if (phase === "intro") loadHistory(); }, [phase]);

  const startTimer = () => {
    startRef.current = Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
  };
  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/adaptive/start", { exam });
      setSid(data.session_id);
      setQ(data.question);
      setLevel(data.question.level);
      setProgress({ index: 0, total: data.question.total, correct: 0, wrong: 0 });
      setFb(null); setSelected(null); setSummary(null); setElapsed(0);
      setPhase("play");
      startTimer();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const answer = async (key) => {
    if (fb || busy) return;
    setSelected(key); setBusy(true);
    try {
      const { data } = await api.post("/adaptive/answer", { session_id: sid, qid: q.qid, answer: key });
      setFb(data);
      setLevel(data.current_level);
      setProgress(data.progress);
    } catch (e) { toast.error(apiErr(e)); setSelected(null); } finally { setBusy(false); }
  };

  const next = () => {
    if (fb?.done) {
      clearInterval(timerRef.current);
      setSummary({ ...fb.summary, time: elapsed });
      setPhase("done");
      return;
    }
    setQ(fb.question); setLevel(fb.question.level); setFb(null); setSelected(null);
  };

  // ---- INTRO
  if (phase === "intro") {
    return (
      <div className="max-w-2xl mx-auto" data-testid="adaptive-intro">
        <div className="card-chunky p-8 text-center">
          <div className="w-16 h-16 rounded-3xl bg-su-primary/15 grid place-items-center mx-auto mb-4"><Brain className="w-9 h-9 text-su-primary" /></div>
          <h1 className="font-heading font-black text-3xl mb-2">Latihan Adaptif</h1>
          <p className="text-su-muted font-medium mb-6">Soal menyesuaikan kemampuanmu secara otomatis: <b>benar 2× berturut naik level</b>, <b>salah turun level</b>. Dari Mudah hingga HOTS — tantangan selalu pas.</p>
          <div className="flex gap-2 justify-center mb-6">
            {["CPNS", "SNBT"].map((x) => (
              <button key={x} data-testid={`adaptive-exam-${x}`} onClick={() => setExam(x)}
                className={`px-6 py-2.5 rounded-xl font-heading font-black border-2 transition-all ${exam === x ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>{x}</button>
            ))}
          </div>
          <Btn data-testid="adaptive-start" onClick={start} disabled={busy} size="lg" className="w-full">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Zap className="w-5 h-5" /> Mulai Latihan (20 Soal)</>}
          </Btn>
        </div>

        {history && history.sessions.length > 0 && (
          <div className="card-chunky p-6 mt-4" data-testid="adaptive-history">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-su-success" /><h2 className="font-heading font-black text-lg">Perkembanganmu</h2></div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <SumCard label="Total Sesi" value={history.stats.total_sessions} testid="adaptive-stat-sessions" />
              <SumCard label="Level Terbaik" value={history.stats.best_level || "-"} cls="text-su-energy" testid="adaptive-stat-best" />
              <SumCard label="Rata Akurasi" value={`${history.stats.avg_accuracy}%`} testid="adaptive-stat-acc" />
            </div>
            <p className="text-xs font-heading font-black uppercase text-su-muted mb-2">Level Tertinggi per Sesi</p>
            <div className="h-52 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history.sessions} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--su-border)" />
                  <XAxis dataKey="n" tick={{ fontSize: 11, fill: "var(--su-muted)" }} tickFormatter={(v) => `#${v}`} />
                  <YAxis domain={[1, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 11, fill: "var(--su-muted)" }}
                    tickFormatter={(v) => ["", "Mudah", "Sedang", "Sulit", "HOTS"][v]} width={58} />
                  <Tooltip contentStyle={{ background: "var(--su-surface)", border: "2px solid var(--su-border)", borderRadius: 12, fontSize: 12 }}
                    formatter={(v, name) => name === "max_level_num" ? [["", "Mudah", "Sedang", "Sulit", "HOTS"][v], "Level tertinggi"] : [v, name]}
                    labelFormatter={(l) => `Sesi #${l}`} />
                  <Line type="monotone" dataKey="max_level_num" stroke="var(--su-primary)" strokeWidth={3} dot={{ r: 4, fill: "var(--su-primary)" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {history.sessions.slice().reverse().slice(0, 5).map((s) => (
                <div key={s.n} className="flex items-center gap-3 rounded-xl border-2 border-su-border px-3 py-2 text-sm" data-testid={`adaptive-hist-${s.n}`}>
                  <span className="font-heading font-black text-su-muted w-10">#{s.n}</span>
                  <span className="text-su-muted font-medium flex-1">{s.date} · {s.exam}</span>
                  <span className={`text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-md ${LEVEL_COLOR[s.max_level]}`}>{s.max_level_label}</span>
                  <span className="font-heading font-black w-12 text-right">{s.accuracy}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- DONE
  if (phase === "done" && summary) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="adaptive-summary">
        <div className="card-chunky p-8 text-center">
          <div className="w-16 h-16 rounded-3xl bg-su-energy/15 grid place-items-center mx-auto mb-4"><Trophy className="w-9 h-9 text-su-energy" /></div>
          <h1 className="font-heading font-black text-3xl mb-1">Latihan Selesai! 🎉</h1>
          <p className="text-su-muted font-medium mb-6">Level tertinggi yang kamu capai: <b className={`px-2 py-0.5 rounded-md ${LEVEL_COLOR[summary.max_level]}`}>{summary.max_level_label}</b></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <SumCard label="Akurasi" value={`${summary.accuracy}%`} testid="adaptive-sum-accuracy" />
            <SumCard label="Benar" value={summary.correct} cls="text-su-success" testid="adaptive-sum-correct" />
            <SumCard label="Salah" value={summary.wrong} cls="text-su-danger" />
            <SumCard label="Waktu" value={fmtTime(summary.time || 0)} />
          </div>
          <div className="mb-6">
            <p className="text-xs font-heading font-black uppercase text-su-muted mb-2">Level Akhir</p>
            <LevelMeter level={summary.final_level} />
          </div>
          <div className="flex gap-2">
            <Btn data-testid="adaptive-restart" onClick={() => setPhase("intro")} className="flex-1"><RotateCcw className="w-4 h-4" /> Latihan Lagi</Btn>
          </div>
        </div>
      </div>
    );
  }

  // ---- PLAY
  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="adaptive-play">
      <div className="card-chunky p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-heading font-black uppercase px-2.5 py-1 rounded-lg ${LEVEL_COLOR[level]}`} data-testid="adaptive-level">{LEVEL_LABEL[level]}</span>
          <span className="text-sm font-heading font-bold text-su-muted">{q?.category}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-sm font-heading font-black text-su-muted"><Clock className="w-4 h-4" /> {fmtTime(elapsed)}</span>
          <span className="text-sm font-heading font-black" data-testid="adaptive-progress">{progress.index}{fb ? "" : "+"} / {progress.total}</span>
        </div>
      </div>

      <LevelMeter level={level} />

      <div className="card-chunky p-6" data-testid="adaptive-question">
        <p className="font-heading font-extrabold text-lg mb-4 whitespace-pre-line">{q?.question_text}</p>
        <div className="space-y-2">
          {q?.options.map((o) => {
            const isPicked = selected === o.key;
            const isAns = fb && o.key === fb.correct_answer;
            const isWrongPick = fb && isPicked && !fb.correct;
            let cls = "border-su-border hover:border-su-primary";
            if (fb) {
              if (isAns) cls = "border-su-success bg-su-success/10";
              else if (isWrongPick) cls = "border-su-danger bg-su-danger/10";
              else cls = "border-su-border opacity-60";
            } else if (isPicked) cls = "border-su-primary bg-su-primary/10";
            return (
              <button key={o.key} data-testid={`adaptive-opt-${o.key}`} disabled={!!fb || busy} onClick={() => answer(o.key)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition-all flex items-center gap-3 ${cls}`}>
                <span className="font-heading font-black">{o.key}.</span>
                <span className="flex-1">{o.text}</span>
                {fb && isAns && <CheckCircle2 className="w-5 h-5 text-su-success" />}
                {isWrongPick && <XCircle className="w-5 h-5 text-su-danger" />}
              </button>
            );
          })}
        </div>
      </div>

      {fb && (
        <div className={`card-chunky p-4 ${fb.correct ? "border-su-success" : "border-su-danger"}`} data-testid="adaptive-feedback">
          <p className={`font-heading font-black mb-1 ${fb.correct ? "text-su-success" : "text-su-danger"}`}>
            {fb.correct ? "Benar! Naik menuju soal lebih menantang 🔥" : `Kurang tepat. Jawaban: ${fb.correct_answer}`}
          </p>
          {fb.explanation && <p className="text-sm text-su-muted font-medium mb-3"><b>Pembahasan:</b> {fb.explanation}</p>}
          <Btn data-testid="adaptive-next" onClick={next} className="w-full">
            {fb.done ? <>Lihat Hasil <Trophy className="w-4 h-4" /></> : <>Soal Berikutnya <ChevronRight className="w-4 h-4" /></>}
          </Btn>
        </div>
      )}
    </div>
  );
}

function LevelMeter({ level }) {
  const idx = LEVELS.indexOf(level);
  return (
    <div className="flex items-center gap-1.5" data-testid="adaptive-level-meter">
      {LEVELS.map((l, i) => (
        <div key={l} className="flex-1">
          <div className={`h-2 rounded-full ${i <= idx ? LEVEL_COLOR[level].split(" ")[1] : "bg-su-border"}`} />
          <p className={`text-[10px] font-heading font-black text-center mt-1 ${i === idx ? LEVEL_COLOR[l].split(" ")[0] : "text-su-muted"}`}>{LEVEL_LABEL[l]}</p>
        </div>
      ))}
    </div>
  );
}

function SumCard({ label, value, cls = "", testid }) {
  return (
    <div className="rounded-2xl border-2 border-su-border p-3" data-testid={testid}>
      <p className="text-[10px] font-heading font-black uppercase text-su-muted">{label}</p>
      <p className={`font-heading font-black text-xl ${cls}`}>{value}</p>
    </div>
  );
}
